import { supabase } from '../../config/supabase';

/**
 * Get jobs that are in production
 */
export const getJobsForProduction = async (companyId, filters = {}) => {
    try {
        const {
            page = 1,
            limit = 20,
            userId = null,
            inProduction = true
        } = filters;

        const offset = (page - 1) * limit;

        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*)
            `, { count: 'exact' })
            .eq('company_id', companyId)
            .eq('deleted', false)
            .order('created_at', { ascending: false });

        // Filter for jobs in production phases
        if (inProduction) {
            // query = query.in('job_production.phase', ['not_started', 'cutting', 'in_production', 'completed']);
            query = query.eq('job_quotes.design_status', "completed");
        }

        // // Apply user filter if provided
        if (userId) {
            query = query.eq('job_production.user_id', userId);
        }

        // Apply pagination
        query = query.range(offset, offset + limit - 1);

        const { data, error, count } = await query;

        if (error) throw error;

        // Transform data to include production info at top level
        const transformedData = data?.map(job => {
            // Find the active production record (there should only be one per job)
            const production = job.job_production?.[0];

            return {
                id: job.id,
                unique_id: job.unique_id,
                customer_name: job.customer_name,
                carpenter_supervisor_name: job.carpenter_supervisor_name,
                job_type: job.job_type,
                main_status: job.main_status,
                urgent_quote: job.urgent_quote,
                urgent_design: job.urgent_design,
                created_at: job.created_at,
                production: production ? {
                    id: production.id,
                    quote_type: production.quote_type,
                    user_id: production.user_id,
                    name: production.name,
                    phase: production.phase,
                    is_started_cutting: production.is_started_cutting,
                    is_started_production: production.is_started_production,
                    is_completed: production.is_completed,
                    start_cutting_at: production.start_cutting_at,
                    end_cutting_at: production.end_cutting_at,
                    start_production_at: production.start_production_at,
                    end_production_at: production.end_production_at,
                    production_notes: production.production_notes
                } : null
            };
        }) || [];

        return {
            data: transformedData,
            pagination: {
                page,
                limit,
                total: count || 0,
                hasMore: count ? (page * limit) < count : false
            }
        };
    } catch (error) {
        console.error('Error fetching production jobs:', error);
        throw error;
    }
};

/**
 * Handle production actions (start/end cutting, start/end production)
 * @param {string} jobId - The job ID
 * @param {string} action - The action to perform (start_cutting, end_cutting, start_production, end_production)
 * @param {string} quoteType - The quote type (roof, wall, floor)
 * @returns {Promise<Object>} Result of the production action
 */
export const handleProductionAction = async (jobId, action, quoteType) => {
    try {
        // Validate action
        const validActions = ['start_cutting', 'end_cutting', 'start_production', 'end_production'];
        if (!validActions.includes(action)) {
            throw new Error('Invalid production action');
        }

        // Validate quoteType is provided
        if (!quoteType) {
            throw new Error('Quote type is required for production actions');
        }

        console.log(`Production action: ${action}, Quote Type: ${quoteType}, Job: ${jobId}`);

        // Get current user
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) {
            throw new Error('User not authenticated');
        }

        const userId = userData.user.id;
        const userName = userData.user.user_metadata?.full_name || userData.user.email;

        // Check if production record exists
        const { data: existing, error: checkError } = await supabase
            .from('job_production')
            .select('*')
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .single();

        if (checkError && checkError.code !== 'PGRST116') {
            throw checkError;
        }

        let updateData = {};
        let phase = existing?.phase || 'not_started';

        // Determine what to update based on action
        switch (action) {
            case 'start_cutting':
                updateData = {
                    is_started_cutting: true,
                    start_cutting_at: new Date().toISOString(),
                    phase: 'in_cutting'
                };
                phase = 'in_cutting';
                break;

            case 'end_cutting':
                updateData = {
                    end_cutting_at: new Date().toISOString(),
                    phase: 'cutting_ended'
                };
                phase = 'cutting_ended';
                break;

            case 'start_production':
                updateData = {
                    is_started_production: true,
                    start_production_at: new Date().toISOString(),
                    phase: 'in_production'
                };
                phase = 'in_production';
                break;

            case 'end_production':
                updateData = {
                    is_completed: true,
                    end_production_at: new Date().toISOString(),
                    phase: 'completed'
                };
                phase = 'completed';
                break;
        }

        let result;

        if (existing) {
            // Update existing production record
            const { data, error } = await supabase
                .from('job_production')
                .update(updateData)
                .eq('job_id', jobId)
                .eq('quote_type', quoteType)
                .select()
                .single();

            if (error) throw error;
            result = data;
        } else {
            // Create new production record
            const { data, error } = await supabase
                .from('job_production')
                .insert({
                    job_id: jobId,
                    quote_type: quoteType,
                    user_id: userId,
                    name: userName,
                    ...updateData
                })
                .select()
                .single();

            if (error) throw error;
            result = data;
        }

        // Log the action
        await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: userId,
                user_name: userName,
                user_role: userData.user.user_metadata?.role || 'admin',
                action: `Production ${action.replace('_', ' ')}`,
                details: `${action.replace('_', ' ')} for ${quoteType} quote`,
                quote_type: quoteType
            });

        console.log(`Production ${action} completed successfully for ${quoteType}`);
        return { success: true, data: result, phase };
    } catch (error) {
        console.error(`Failed to ${action} production:`, error);
        throw error;
    }
};


/**
 * Subscribe to real-time production updates
 * Listens to changes in jobs, job_quotes, job_production, and job_designers tables
 * @param {string} companyId - The company ID
 * @param {Function} callback - Callback function for updates
 * @returns {Object} Supabase channel subscription
 */
export const subscribeToProductionUpdates = (companyId, callback) => {
    const channel = supabase
        .channel(`production-changes-${companyId}`)
        // Listen to job changes
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'jobs',
                filter: `company_id=eq.${companyId}`
            },
            (payload) => callback({ table: 'jobs', ...payload })
        )
        // Listen to job_quotes changes (design status changes)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_quotes'
            },
            (payload) => callback({ table: 'job_quotes', ...payload })
        )
        // Listen to job_production changes (production phase changes)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_production'
            },
            (payload) => callback({ table: 'job_production', ...payload })
        )
        // Listen to job_designers changes (designer assignments)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_designers'
            },
            (payload) => callback({ table: 'job_designers', ...payload })
        )
        // Listen to job_estimators changes
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_estimators'
            },
            (payload) => callback({ table: 'job_estimators', ...payload })
        )
        .subscribe();

    return channel;
};

/**
 * Unsubscribe from real-time updates
 * @param {Object} channel - The Supabase channel subscription
 */
export const unsubscribeFromProductionUpdates = async (channel) => {
    if (channel) {
        await supabase.removeChannel(channel);
    }
};